import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Container, Row, Col, Button, Alert } from 'reactstrap';
import moment from 'moment';

import api from '../services/api';
import EditBookingModal from './EditBookingModal';
import withWebSocket from '../contexts/withWebSocket';
import EventGroup from './EventGroup';
import CheckInModal from './CheckInModal';
import { useReferenceData, useDailyEvents, useCheckInStates } from '../hooks/useScheduleData';

const UpcomingEvents = ({
  selectedDate = new Date(),
  onSubmit,
  onToday,
  showToday,
  gridSize,
  refreshToken,
  needsRefresh = 0,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [event, setEvent] = useState({});
  const [notes, setNotes] = useState('');
  const [editEvent, setEditEvent] = useState(false);

  const { resources: simulators, discountCodes } = useReferenceData();
  const { events, reload } = useDailyEvents(selectedDate, { refreshToken });
  const checkInState = useCheckInStates();

  useEffect(() => {
    if (needsRefresh > 0) {
      reload();
    }
  }, [needsRefresh, reload]);

  const toggleModal = useCallback((currentEvent = null) => {
    setModalOpen((open) => !open);
    setSelectedEvent(currentEvent);
    setNotes(currentEvent?.notes || '');
  }, []);

  const handleBookingClick = useCallback(async (id) => {
    try {
      const res = await api.get(`/events/${id}`);
      setEvent(res.data);
      setEditEvent(true);
    } catch (error) {
      console.error('Failed to fetch event:', error);
    }
  }, []);

  const handleClose = useCallback(() => {
    setEditEvent(false);
  }, []);

  const handleSubmit = useCallback((date) => {
    handleClose();
    if (onSubmit) {
      onSubmit(date || selectedDate);
    }
  }, [handleClose, onSubmit, selectedDate]);

  const confirmCheckIn = useCallback((status) => {
    if (!selectedEvent) return;

    const isNoShow = status === 'noshow';
    const hasCheckInStatus = !!status && !isNoShow;

    const payload = {
      check_in_state: status ? status.id : null,
      notes: notes || '',
      state: isNoShow ? 'NoShow' : 'Booked',
      resourceLocked: hasCheckInStatus ? true : selectedEvent.resourceLocked,
    };

    api.patch(`/events/${selectedEvent.id}/`, payload)
      .then(() => {
        setModalOpen(false);
        setSelectedEvent(null);
        if (onSubmit) {
          onSubmit(selectedDate);
        }
      })
      .catch(err => console.error('Failed to update check-in:', err));
  }, [notes, onSubmit, selectedDate, selectedEvent]);

  const groupedEvents = useMemo(() => {
    const groups = events.reduce((acc, current) => {
      const eventEnd = moment(current.end).utc();
      const now = moment().utc();
      const checkIn = current.checkInDetails;

      let state;
      if (checkIn?.final) state = 'Closed';
      else if (current.state === 'NoShow') state = 'Closed';
      else if (checkIn) state = 'In Progress';
      else if (eventEnd.isBefore(now)) state = 'Past';
      else state = 'Upcoming';

      (acc[state] = acc[state] || []).push(current);
      return acc;
    }, {});

    const order = ['In Progress', 'Upcoming', 'Closed', 'Past'];
    return order.reduce((sorted, key) => {
      if (groups[key]) {
        sorted[key] = groups[key].sort((a, b) => moment(a.start).diff(moment(b.start)));
      }
      return sorted;
    }, {});
  }, [events]);

  return (
    <Container fluid className="d-flex flex-column m-0 p-3 h-100">
      <Row className="mb-0">
        <Col>
          <h5>
            {moment(selectedDate).format('dddd MMMM Do')}
            {showToday && (
              <Button color="link" className="text-secondary" onClick={onToday}>
                <small>Go To Today</small>
              </Button>
            )}
          </h5>
        </Col>
      </Row>

      {events.length === 0 ? (
        <Alert color="light" fade={false}>No scheduled events</Alert>
      ) : (
        Object.entries(groupedEvents).map(([state, group], idx) => (
          <EventGroup
            key={idx}
            title={state}
            events={group}
            gridSize={gridSize}
            onEdit={handleBookingClick}
            onCheckIn={toggleModal}
          />
        ))
      )}

      <CheckInModal
        isOpen={modalOpen}
        toggle={() => toggleModal()}
        selectedEvent={selectedEvent}
        checkInState={checkInState}
        notes={notes}
        setNotes={setNotes}
        confirmCheckIn={confirmCheckIn}
      />

      <EditBookingModal
        key={event.id}
        open={editEvent}
        event={event}
        bookingDate={moment(selectedDate)}
        bookingTime={event.start}
        service={event.service}
        resources={simulators}
        discountCodes={discountCodes}
        onClose={handleClose}
        onToday={onToday}
        onSubmit={handleSubmit}
      />
    </Container>
  );
};

export default withWebSocket(UpcomingEvents);
